/**
 * Peças visuais da Supervisão (spec 071), no padrão do Hoje: cartão de número,
 * seção com título e o (i) com a definição de cada número — a definição na
 * tela é a da spec, e quem mudar uma muda a outra.
 */
import type { ComponentType, ReactNode } from 'react';
import { Info } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';

export const TH = 'h-9 px-4 text-left text-[13px] font-semibold whitespace-nowrap';

export function InfoTip({ children }: { children: ReactNode }) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          aria-label="Como este número é contado"
          className="ml-1 inline-flex shrink-0 cursor-help align-middle text-muted-foreground/70 transition-colors hover:text-muted-foreground"
        >
          <Info className="size-3.5" />
        </button>
      </TooltipTrigger>
      <TooltipContent side="top" className="max-w-72 font-normal">
        {children}
      </TooltipContent>
    </Tooltip>
  );
}

export function StatTile({
  icon: Icon,
  label,
  value,
  valueSuffix,
  detail,
  info,
  tone,
}: {
  icon: ComponentType<{ className?: string }>;
  label: string;
  value: number | string;
  valueSuffix?: string;
  detail?: string;
  info: string;
  /** classe de cor do número (a do nível de espera); ausente = neutro */
  tone?: string | undefined;
}) {
  const muted = value === 0 || value === '—';
  return (
    <div className="flex flex-col gap-1 rounded-lg border bg-card p-4 shadow-sm">
      <span className="flex items-center gap-2 text-[13px] text-muted-foreground">
        <Icon className="size-4" />
        {label}
        <InfoTip>{info}</InfoTip>
      </span>
      <span
        className={cn(
          'text-2xl font-semibold tabular-nums',
          tone ?? (muted ? 'text-muted-foreground/60' : 'text-foreground')
        )}
      >
        {value}
        {valueSuffix ? (
          <span className="ml-1.5 text-sm font-normal text-muted-foreground">{valueSuffix}</span>
        ) : null}
      </span>
      {detail ? <span className="text-[12px] text-muted-foreground">{detail}</span> : null}
    </div>
  );
}

export function Section({
  title,
  count,
  hint,
  empty,
  actions,
  children,
}: {
  title: string;
  count?: number;
  hint?: string;
  empty?: string;
  actions?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section className="overflow-hidden rounded-lg border bg-card shadow-sm" aria-label={title}>
      <header className="flex flex-wrap items-center justify-between gap-2 border-b px-4 py-3">
        <h2 className="text-sm font-semibold">
          {title}
          {count !== undefined ? (
            <span className="ml-2 font-normal text-muted-foreground tabular-nums">{count}</span>
          ) : null}
        </h2>
        <div className="flex items-center gap-3 text-xs text-muted-foreground">
          {hint ? <span>{hint}</span> : null}
          {actions}
        </div>
      </header>
      {count === 0 && empty ? (
        <p className="px-4 py-8 text-center text-sm text-muted-foreground">{empty}</p>
      ) : null}
      <div className="overflow-x-auto">{children}</div>
    </section>
  );
}
